/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Component, Input, OnInit, OnChanges, SimpleChanges, ElementRef, ViewChild, AfterViewInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { marked } from 'marked';
import hljs from 'highlight.js';
import { DocumentationError } from '../../../api-mesh/models/documentation.interface';

/**
 * Component for rendering Markdown content with syntax highlighting
 * Supports internal links, code highlighting, and responsive design
 */
@Component({
  selector: 'app-markdown-renderer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './markdown-renderer.component.html',
  styleUrls: [
    './markdown-renderer.component.scss'
  ],
  encapsulation: ViewEncapsulation.None
})
export class MarkdownRendererComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() content: string = '';
  @Input() baseRoute: string = '/api-mesh/docs';
  @Input() loading: boolean = false;
  @Input() error: DocumentationError | null = null;
  @Input() enableToc: boolean = true;

  @ViewChild('markdownContainer', { static: false }) markdownContainer!: ElementRef<HTMLDivElement>;

  renderedHtml: SafeHtml = '';
  tableOfContents: TocItem[] = [];
  showToc: boolean = false;

  private clickListener?: (event: Event) => void;
  private isProcessingLinkText: boolean = false;

  constructor(
    private sanitizer: DomSanitizer,
    private router: Router
  ) {
    this.configureMarked();
  }

  ngOnInit(): void {
    this.renderMarkdown();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['content'] && !changes['content'].firstChange) {
      this.renderMarkdown();
    }
  }

  ngAfterViewInit(): void {
    this.setupLinkHandling();
  }

  ngOnDestroy(): void {
    this.removeClickListener();
  }

  /**
   * Configure marked.js with custom options and renderer
   */
  private configureMarked(): void {
    const renderer = new marked.Renderer();

    // Custom heading renderer for table of contents
    renderer.heading = (token: any) => {
      const text = typeof token === 'string' ? token : token.text || token.raw;
      const level = typeof token === 'object' ? token.depth : arguments[1];
      const id = this.generateId(text);

      // Process inline formatting in heading text
      const processedText = this.processInlineFormatting(text);

      if (this.enableToc && level <= 3) {
        this.tableOfContents.push({
          id,
          text: processedText,
          level,
          raw: text
        });
      }

      return `<h${level} id="${id}" class="markdown-heading markdown-h${level}">${processedText}</h${level}>`;
    };

    // Custom link renderer for internal navigation
    renderer.link = (token: any) => {
      const href = typeof token === 'string' ? token : token.href;
      const title = typeof token === 'object' ? token.title : arguments[1];
      const text = typeof token === 'object' ? token.text : arguments[2];

      // Process inline markdown formatting within link text, but prevent recursion
      let processedText = text;
      if (!this.isProcessingLinkText) {
        try {
          this.isProcessingLinkText = true;
          processedText = this.processInlineFormatting(text);
        } finally {
          this.isProcessingLinkText = false;
        }
      }

      if (this.isInternalLink(href)) {
        const route = this.convertToRoute(href);
        return `<a href="${route}" class="internal-link" title="${title || ''}">${processedText}</a>`;
      }

      // External links open in new tab
      if (this.isExternalLink(href)) {
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" title="${title || ''}">${processedText}</a>`;
      }

      return `<a href="${href}" title="${title || ''}">${processedText}</a>`;
    };

    // Custom code renderer with syntax highlighting
    renderer.code = (token: any) => {
      const code = typeof token === 'string' ? token : token.text || token.raw;
      const language = typeof token === 'object' ? token.lang : arguments[1];

      if (language && hljs.getLanguage(language)) {
        try {
          const highlighted = hljs.highlight(code, { language }).value;
          return `<pre class="hljs markdown-code"><code class="language-${language}">${highlighted}</code></pre>`;
        } catch (error) {
          console.warn('Syntax highlighting failed:', error);
        }
      }

      return `<pre class="markdown-code"><code>${this.escapeHtml(code)}</code></pre>`;
    };

    marked.setOptions({
      renderer,
      gfm: true,
      breaks: true
    });
  }

  /**
   * Render markdown content to HTML
   */
  private renderMarkdown(): void {
    if (!this.content) {
      this.renderedHtml = '';
      this.tableOfContents = [];
      return;
    }

    try {
      // Reset TOC for new content
      this.tableOfContents = [];

      // Process markdown
      const rawHtml = marked.parse(this.content) as string;

      // Sanitize and trust the HTML
      this.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(rawHtml);

      // Show TOC if we have headings
      this.showToc = this.enableToc && this.tableOfContents.length > 0;

    } catch (error) {
      console.error('Markdown rendering failed:', error);
      this.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(
        `<div class="error-message">Failed to render markdown content: ${(error as Error).message}</div>`
      );
    }
  }

  /**
   * Setup click handling for internal links
   */
  private setupLinkHandling(): void {
    this.removeClickListener();

    this.clickListener = (event: Event) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a.internal-link') as HTMLAnchorElement;

      if (link) {
        event.preventDefault();
        const route = link.getAttribute('href');
        if (route) {
          this.router.navigate([route]);
        }
      }
    };

    if (this.markdownContainer?.nativeElement) {
      this.markdownContainer.nativeElement.addEventListener('click', this.clickListener);
    }
  }

  /**
   * Remove click event listener
   */
  private removeClickListener(): void {
    if (this.clickListener && this.markdownContainer?.nativeElement) {
      this.markdownContainer.nativeElement.removeEventListener('click', this.clickListener);
    }
  }

  /**
   * Scroll to heading by ID
   */
  scrollToHeading(id: string): void {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Generate ID from heading text
   */
  private generateId(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .trim();
  }

  /**
   * Check if link is internal (relative or hash)
   */
  private isInternalLink(href: string): boolean {
    return href.startsWith('/') || href.startsWith('#') || href.startsWith('./') || href.startsWith('../');
  }

  /**
   * Check if link is external
   */
  private isExternalLink(href: string): boolean {
    return href.startsWith('http://') || href.startsWith('https://');
  }

  /**
   * Convert relative link to router-compatible route
   */
  private convertToRoute(href: string): string {
    if (href.startsWith('#')) {
      return href; // Hash links stay as-is
    }

    if (href.startsWith('./') || href.startsWith('../')) {
      // Convert relative paths to absolute routes
      const cleanPath = href.replace(/^\.\.?\//, '');
      return `${this.baseRoute}/${cleanPath}`;
    }

    if (href.startsWith('/')) {
      return href; // Absolute paths stay as-is
    }

    return href;
  }

  /**
   * Escape HTML characters
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Process inline markdown formatting without recursion
   */
  private processInlineFormatting(text: string): string {
    if (!text) return text;

    // Process bold (**text** or __text__)
    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/__([^_]+)__/g, '<strong>$1</strong>');

    // Process italic (*text* or _text_)
    text = text.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    text = text.replace(/_([^_]+)_/g, '<em>$1</em>');

    // Process inline code (`text`)
    text = text.replace(/`([^`]+)`/g, '<code>$1</code>');

    return text;
  }

  /**
   * Toggle table of contents visibility
   */
  toggleToc(): void {
    this.showToc = !this.showToc;
  }
}

/**
 * Interface for table of contents items
 */
interface TocItem {
  id: string;
  text: string;
  level: number;
  raw: string;
}
